import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { AuthShell, inputCls, primaryBtnCls } from "@/components/AuthShell";
import { errMsg, http } from "@/lib/api";
import { usePageTitle } from "@/lib/hooks";

export default function ResetPasswordPage() {
  usePageTitle("Reset Password");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? "" : "This reset link is missing its code. Request a new one.");
  const strong = pw.length >= 8 && /[A-Za-z]/.test(pw) && /\d/.test(pw);

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) { setError("Use at least 8 characters with letters and numbers"); return; }
    if (pw !== confirm) { setError("The new passwords don't match"); return; }
    setBusy(true);
    setError("");
    try {
      await http.post("/auth/reset-password", { token, new_password: pw });
      localStorage.removeItem("ph_token");
      setDone(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthShell testId="reset-password-done" title="Password updated" subtitle="Every device signed in as admin has been logged out.">
        <p className="mt-8 flex items-center gap-2 rounded-2xl bg-ph-lime/15 px-5 py-4 text-sm font-semibold text-ph-ink">
          <CheckCircle2 className="h-4 w-4" /> You can now sign in with your new password.
        </p>
        <Link to="/admin/login" data-testid="reset-password-signin" className={primaryBtnCls}>Go to sign in</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell onSubmit={submit} testId="reset-password-form" title="New password" subtitle="Choose a new admin password.">
      <label className="mt-8 block text-sm font-medium text-ph-ink/85" htmlFor="reset-new">New password</label>
      <input id="reset-new" type="password" required value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="reset-password-new" className={inputCls} />
      <p className={`mt-2 text-xs ${pw ? (strong ? "text-emerald-700" : "text-amber-700") : "text-ph-muted"}`}>At least 8 characters, including letters and numbers.</p>
      <label className="mt-5 block text-sm font-medium text-ph-ink/85" htmlFor="reset-confirm">Confirm new password</label>
      <input id="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="reset-password-confirm" className={inputCls} />
      {error && <p className="mt-4 rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-600" data-testid="reset-password-error">{error}</p>}
      <button type="submit" disabled={busy || !token} data-testid="reset-password-submit" className={primaryBtnCls}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Save new password
      </button>
      <Link to="/admin/forgot-password" data-testid="reset-password-request-new" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-ph-muted hover:text-ph-accent">
        <ArrowLeft className="h-4 w-4" /> Request a new link
      </Link>
    </AuthShell>
  );
}
